// src/components/SonoVault.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';
import { SCAN_ENUMS } from './ReportWriter';

// Map category labels → SCAN_ENUMS values (single source of truth)
const LABEL_TO_ENUM = SCAN_ENUMS.reduce((acc, s) => {
  acc[s.label] = s.value;
  return acc;
}, {});

const enumFor = (label) => LABEL_TO_ENUM[label] || label;

// Same base URL the api service uses (needed to fetch raw file blobs)
const API_BASE =
  process.env.REACT_APP_API_URL ||
  'https://echionhealthapp-production.up.railway.app/api';

// ─── Small helpers ──────────────────────────────────────────────────────────

const getUid = () => {
  try {
    return JSON.parse(localStorage.getItem('user'))?.id ?? 'anon';
  } catch {
    return 'anon';
  }
};
const lsKey = (name) => `sonovault:${getUid()}:${name}`;
const readLS = (name, fallback) => {
  try {
    const v = localStorage.getItem(lsKey(name));
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};
const writeLS = (name, value) => {
  try {
    localStorage.setItem(lsKey(name), JSON.stringify(value));
  } catch {
    /* storage full / unavailable – ignore */
  }
};

const ROOT = 'root';
const folderKeyOf = (id) => (id === null || id === undefined ? ROOT : String(id));

const scopeKey = (scanEnum, folderId) => `${scanEnum}::${folderKeyOf(folderId)}`;

const stripTags = (str = '') => String(str).replace(/<[^>]+>/g, '');

const isLocalId = (id) => String(id).startsWith('local-');

const mapReport = (r) => ({
  id: r.id,
  title: r.patientName || r.originalFilename || 'Untitled Report',
  filename: r.originalFilename || r.filename || null,
  isUpload: Boolean(r.originalFilename || r.filename || r.status === 'UPLOADED'),
  type: r.scanType,
  content: r.findings
    ? stripTags(r.findings).substring(0, 60) + '...'
    : 'No findings recorded.',
  fullContent: `${stripTags(r.findings || '')}\n\nImpression: ${stripTags(
    r.impression || ''
  )}\n\nRecommendation: ${stripTags(r.recommendation || '')}`,
  patientName: r.patientName,
  scanDate: r.scanDate,
  status: r.status,
});

const applyRenames = (items) => {
  const renames = readLS('renames', {});
  return items.map((i) =>
    renames[String(i.id)] ? { ...i, title: renames[String(i.id)] } : i
  );
};

const reportFolderId = (r) => r.folderId ?? r.folder?.id ?? r.vaultFolderId ?? null;

// ─── Backend request helpers ────────────────────────────────────────────────

const vaultRequest = async (method, path, body) => {
  const token = localStorage.getItem('authToken');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Accept: '*/*',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const raw = await res.text();
  let json = null;
  try {
    json = raw ? JSON.parse(raw) : null;
  } catch {
    /* not JSON */
  }

  if (!res.ok) {
    const msg = (json && json.message) || raw || 'Request failed';
    throw new Error(`${msg} (${res.status})`);
  }
  if (json && json.success === false) {
    throw new Error(json.message || 'Request failed');
  }
  return json;
};

const EDITABLE_FIELDS = [
  'patientName',
  'patientAge',
  'patientSex',
  'patientId',
  'scanDate',
  'scanType',
  'reportType',
  'clinicalHistory',
  'findings',
  'impression',
  'recommendation',
  'structuredFindings',
  'recommendationOptions',
  'tags',
  'isFavorite',
];

const pickEditable = (obj = {}) => {
  const out = {};
  EDITABLE_FIELDS.forEach((k) => {
    if (obj[k] !== undefined && obj[k] !== null) out[k] = obj[k];
  });
  return out;
};

const removeLocalItem = (id) => {
  const idStr = String(id);

  const uploads = readLS('uploads', {});
  let changed = false;
  Object.keys(uploads).forEach((key) => {
    const before = uploads[key] || [];
    const after = before.filter((i) => String(i.id) !== idStr);
    if (after.length !== before.length) {
      uploads[key] = after;
      changed = true;
    }
  });
  if (changed) writeLS('uploads', uploads);

  const renames = readLS('renames', {});
  if (renames[idStr]) {
    delete renames[idStr];
    writeLS('renames', renames);
  }
};

// ─── Folder bookkeeping (category + parent) ────────────────────────────────

const rememberFolder = (folderId, enumValue, parentId) => {
  if (folderId === undefined || folderId === null) return;
  const cats = readLS('folderCategory', {});
  const parents = readLS('folderParent', {});
  cats[folderId] = enumValue;
  parents[folderId] = folderKeyOf(parentId);
  writeLS('folderCategory', cats);
  writeLS('folderParent', parents);
};

// Which folders belong to the current view (category + parent)
const scopeFolders = (list, enumValue, parentId) => {
  const cats = readLS('folderCategory', {});
  const parents = readLS('folderParent', {});
  const here = folderKeyOf(parentId);
  let changed = false;
  const visible = [];

  (Array.isArray(list) ? list : []).forEach((f) => {
    if (!f || f.id === undefined || f.id === null) return;

    const cat = cats[f.id] || f.scanType || f.category || null;
    const par = parents[f.id] || null;

    if (cat && cat !== enumValue) return;
    if (par && par !== here) return;

    if (!cats[f.id]) {
      cats[f.id] = enumValue;
      changed = true;
    }
    if (!parents[f.id]) {
      parents[f.id] = here;
      changed = true;
    }
    visible.push(f);
  });

  if (changed) {
    writeLS('folderCategory', cats);
    writeLS('folderParent', parents);
  }
  return visible;
};

// Item count for each folder = files uploaded into it + subfolders inside it.
const withItemCounts = (list, enumValue) => {
  const uploads = readLS('uploads', {});
  const parents = readLS('folderParent', {});
  return list.map((f) => {
    const files = (uploads[scopeKey(enumValue, f.id)] || []).length;
    const subs = Object.keys(parents).filter((k) => parents[k] === String(f.id)).length;
    const server = Number(f.reportCount) || 0;
    return { ...f, reportCount: Math.max(server, files) + subs };
  });
};

// ─── File bookkeeping (where each upload lives) ─────────────────────────────

const buildPlacement = () => {
  const uploads = readLS('uploads', {});
  const placement = {};
  Object.keys(uploads).forEach((key) => {
    (uploads[key] || []).forEach((item) => {
      placement[String(item.id)] = key;
    });
  });
  return placement;
};

const filterServerItems = (list, enumValue, folderId) => {
  const placement = buildPlacement();
  const here = scopeKey(enumValue, folderId);
  const hereFolder = folderKeyOf(folderId);

  return (Array.isArray(list) ? list : []).filter((r) => {
    const placed = placement[String(r.id)];
    if (placed) return placed === here;

    if (r.scanType && enumValue && r.scanType !== enumValue) return false;

    return folderKeyOf(reportFolderId(r)) === hereFolder;
  });
};

// ─── Recursively collect a folder + every subfolder under it ────────────────
// Returns an array of folder id strings (including `rootId`).
const collectFolderTree = (rootId) => {
  const parents = readLS('folderParent', {});
  const ids = [String(rootId)];
  const queue = [String(rootId)];
  const seen = new Set(ids);

  while (queue.length) {
    const cur = queue.shift();
    Object.keys(parents).forEach((child) => {
      if (parents[child] === cur && !seen.has(child)) {
        seen.add(child);
        ids.push(child);
        queue.push(child);
      }
    });
  }
  return ids;
};

// Remove a folder (and everything underneath it) from local storage.
const purgeFolderLocally = (folderId) => {
  const idStr = String(folderId);

  // 1. Collect this folder + all descendants
  const idsToRemove = collectFolderTree(idStr);
  const removeSet = new Set(idsToRemove.map(String));

  // 2. Drop folders from the category / parent maps
  const cats = readLS('folderCategory', {});
  const parents = readLS('folderParent', {});
  idsToRemove.forEach((id) => {
    delete cats[id];
    delete parents[id];
  });
  writeLS('folderCategory', cats);
  writeLS('folderParent', parents);

  // 3. Drop any uploads that lived inside any of those folders
  const uploads = readLS('uploads', {});
  let uploadsChanged = false;
  Object.keys(uploads).forEach((key) => {
    // key format: `${scanEnum}::${folderKeyOf(folderId)}`
    const parts = key.split('::');
    const folderPart = parts[1];
    if (folderPart && removeSet.has(folderPart)) {
      delete uploads[key];
      uploadsChanged = true;
    }
  });
  if (uploadsChanged) writeLS('uploads', uploads);

  // 4. Drop any locally remembered renames for files we just removed
  const renames = readLS('renames', {});
  const removedFileIds = new Set();
  Object.keys(uploads).forEach((key) => {
    (uploads[key] || []).forEach((it) => removedFileIds.add(String(it.id)));
  });
  Object.keys(renames).forEach((rid) => {
    if (!removedFileIds.has(rid) && !renames[rid]) return;
  });

  return idsToRemove;
};

// ─── File preview helpers ───────────────────────────────────────────────────

const fetchFileBlob = async (path) => {
  const token = localStorage.getItem('authToken');
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: {
      Accept: '*/*',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.blob();
};

const detectKind = (type = '', name = '') => {
  const t = String(type).toLowerCase();
  const n = String(name).toLowerCase();
  if (t.includes('pdf') || n.endsWith('.pdf')) return 'pdf';
  if (t.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(n)) return 'image';
  if (t.startsWith('text/') || n.endsWith('.txt')) return 'text';
  return 'other';
};

const buildViewer = async (blob, filename, created) => {
  let kind = detectKind(blob.type, filename);

  if (kind === 'other') {
    try {
      const head = await blob.slice(0, 5).text();
      if (head.startsWith('%PDF')) kind = 'pdf';
    } catch {
      /* ignore */
    }
  }

  let finalBlob = blob;
  if (kind === 'pdf' && blob.type !== 'application/pdf') {
    finalBlob = new Blob([blob], { type: 'application/pdf' });
  }

  const url = URL.createObjectURL(finalBlob);
  created.push(url);
  const text = kind === 'text' ? await finalBlob.text() : '';
  return { kind, url, text };
};

// ─── Viewport hook ───
const useViewport = () => {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return {
    width,
    isMobile: width < 640,
    isTablet: width >= 640 && width < 1024,
    isDesktop: width >= 1024,
  };
};

const Svg = ({ size = 20, children, ...rest }) => (
  <svg
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
    {...rest}
  >
    {children}
  </svg>
);

const MENU_ITEMS = [
  {
    tab: 'report',
    label: 'New Report',
    d: 'M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  },
  {
    tab: 'templates',
    label: 'Customize Template',
    d: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z',
  },
  {
    tab: 'saved',
    label: 'Saved Reports',
    d: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4',
  },
  { tab: 'sono-ai', label: 'Sono AI', d: 'M13 10V3L4 14h7v7l9-11h-7z' },
  {
    tab: 'analytics',
    label: 'Analytics',
    d: 'M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z',
  },
];

const VIEWER_INITIAL = { status: 'idle', kind: null, url: null, text: '', message: '' };

const EDIT_FORM_INITIAL = {
  name: '',
  patientName: '',
  patientAge: '',
  patientSex: '',
  patientId: '',
  scanDate: '',
  clinicalHistory: '',
  findings: '',
  impression: '',
  recommendation: '',
};

const SonoVault = ({ onBack, onNavigate, setActiveTab, setTab, onTabChange }) => {
  const { isMobile, isTablet, isDesktop } = useViewport();

  // ─── Navigation & data states ───
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([{ id: null, name: 'Abdominal' }]);

  const [activeCategory, setActiveCategory] = useState('Abdominal');
  const [loading, setLoading] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  const [viewer, setViewer] = useState(VIEWER_INITIAL);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCategoryDrawerOpen, setIsCategoryDrawerOpen] = useState(false);

  // Folder Creation Modal
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDesc, setNewFolderDesc] = useState('');

  // Delete confirmation (reports, files AND folders)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Edit modal
  const [editTarget, setEditTarget] = useState(null);
  const [editFull, setEditFull] = useState(null);
  const [editForm, setEditForm] = useState(EDIT_FORM_INITIAL);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const fileInputRef = useRef(null);
  const loadSeq = useRef(0);
  const localFilesRef = useRef({});

  const scanEnum = enumFor(activeCategory);
  const currentFolder = folderPath[folderPath.length - 1];
  const insideFolder = currentFolderId !== null;

  // ─── Hide global sidebar / top bar while SonoVault is mounted ───
  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const styleId = 'sonovault-fullscreen-override';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        body aside.sidebar,
        body .app-container > aside.sidebar,
        body .sidebar { display: none !important; }
        body .mobile-header { display: none !important; }
        body .top-bar { display: none !important; }
        body .main-content {
          width: 100vw !important;
          max-width: 100vw !important;
          height: 100vh !important;
          overflow: hidden !important;
        }
        body .content-area,
        body .workspace {
          padding: 0 !important;
          margin: 0 !important;
          width: 100vw !important;
          max-width: 100vw !important;
          height: 100vh !important;
          overflow: hidden !important;
        }
      `;
      document.head.appendChild(style);
    }

    const sidebarEl = document.querySelector('aside.sidebar');
    const mobileHeaderEl = document.querySelector('.mobile-header');
    const topBarEl = document.querySelector('.top-bar');
    const prevSidebarDisplay = sidebarEl ? sidebarEl.style.display : '';
    const prevMobileDisplay = mobileHeaderEl ? mobileHeaderEl.style.display : '';
    const prevTopBarDisplay = topBarEl ? topBarEl.style.display : '';
    if (sidebarEl) sidebarEl.style.display = 'none';
    if (mobileHeaderEl) mobileHeaderEl.style.display = 'none';
    if (topBarEl) topBarEl.style.display = 'none';

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      if (sidebarEl) sidebarEl.style.display = prevSidebarDisplay;
      if (mobileHeaderEl) mobileHeaderEl.style.display = prevMobileDisplay;
      if (topBarEl) topBarEl.style.display = prevTopBarDisplay;
      const tag = document.getElementById(styleId);
      if (tag) tag.remove();
    };
  }, []);

  useEffect(() => {
    const workspace = document.querySelector('.workspace');
    if (workspace) workspace.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ─── Load the REAL file whenever a preview is opened ───
  useEffect(() => {
    if (!previewReport) {
      setViewer(VIEWER_INITIAL);
      return undefined;
    }

    let cancelled = false;
    const created = [];

    const run = async () => {
      setViewer({ ...VIEWER_INITIAL, status: 'loading' });

      const idStr = String(previewReport.id);
      const filename = previewReport.filename || previewReport.title || 'document';
      const hasServerId = !idStr.startsWith('local-');
      let fallbackUrl = null;

      const localFile = localFilesRef.current[idStr];
      if (localFile) {
        try {
          const v = await buildViewer(localFile, localFile.name || filename, created);
          if (cancelled) return;
          if (v.kind !== 'other') {
            setViewer({ status: 'ready', ...v, message: '' });
            return;
          }
          fallbackUrl = v.url;
        } catch (err) {
          console.warn('[SonoVault] Local preview failed:', err.message);
        }
      }

      if (hasServerId) {
        const candidates = previewReport.isUpload
          ? [
              `/vault/reports/${idStr}/download`,
              `/vault/reports/${idStr}/download-pdf`,
              `/vault/reports/${idStr}/print`,
            ]
          : [
              `/vault/reports/${idStr}/print`,
              `/vault/reports/${idStr}/download-pdf`,
              `/vault/reports/${idStr}/download`,
            ];

        for (const path of candidates) {
          try {
            const blob = await fetchFileBlob(path);
            if (cancelled) return;
            if (!blob || blob.size === 0) continue;

            const v = await buildViewer(blob, filename, created);
            if (cancelled) return;
            if (v.kind !== 'other') {
              setViewer({ status: 'ready', ...v, message: '' });
              return;
            }
            if (!fallbackUrl) fallbackUrl = v.url;
          } catch (err) {
            console.warn(`[SonoVault] ${path} unavailable:`, err.message);
          }
        }
      }

      if (cancelled) return;

      if (fallbackUrl) {
        setViewer({
          status: 'ready',
          kind: 'other',
          url: fallbackUrl,
          text: '',
          message:
            'This file type cannot be previewed in the browser. Use Download to open it in its own application.',
        });
        return;
      }

      if (!previewReport.isUpload && previewReport.fullContent) {
        setViewer({
          status: 'ready',
          kind: 'text',
          url: null,
          text: previewReport.fullContent,
          message: '',
        });
        return;
      }

      setViewer({
        status: 'error',
        kind: null,
        url: null,
        text: '',
        message:
          'The original file could not be loaded. It may have been uploaded before file storage was available, or the server is unreachable.',
      });
    };

    run();

    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [previewReport]);

  // ─── Fetch reports / uploaded documents for a category + folder ───
  const fetchCategoryReports = useCallback(async (categoryLabel, folderId) => {
    const enumValue = enumFor(categoryLabel);
    let serverItems = [];

    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const filters = { scanType: enumValue, page: 0, size: 100 };
        if (folderId !== null) filters.folderId = folderId;

        const res = await api.searchReportsPage(filters);
        const list = res?.data?.content || res?.data || [];

        serverItems = filterServerItems(list, enumValue, folderId).map(mapReport);
      } else {
        console.warn('[SonoVault] No authToken — skipping backend fetch');
      }
    } catch (err) {
      console.error('[SonoVault] Failed to fetch reports:', err.message);
    }

    const localMap = readLS('uploads', {});
    const localItems = localMap[scopeKey(enumValue, folderId)] || [];
    const serverIds = new Set(serverItems.map((i) => String(i.id)));
    const merged = [
      ...localItems.filter((i) => !serverIds.has(String(i.id))),
      ...serverItems,
    ];

    return applyRenames(merged);
  }, []);

  // ─── Load folders + items for the current category / folder ───
  const loadView = useCallback(async () => {
    const seq = ++loadSeq.current;

    if (keyword) {
      setFolders([]);
      return;
    }

    setLoading(true);
    try {
      let rawFolders = [];
      try {
        if (currentFolderId === null) {
          const folderRes = await api.getRootFolders(scanEnum);
          rawFolders = folderRes?.data || [];
        } else {
          const subRes = await api.getSubfolders(currentFolderId, scanEnum);
          rawFolders = subRes?.data || [];
        }
      } catch (err) {
        console.error('[SonoVault] Failed to load folders:', err.message);
      }

      const items = await fetchCategoryReports(activeCategory, currentFolderId);

      if (seq !== loadSeq.current) return;

      const scoped = scopeFolders(rawFolders, scanEnum, currentFolderId);
      setFolders(withItemCounts(scoped, scanEnum));
      setResults(items);
    } catch (err) {
      console.error('[SonoVault] Error loading view:', err);
      if (seq === loadSeq.current) {
        setFolders([]);
        setResults([]);
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, currentFolderId, keyword, scanEnum, fetchCategoryReports]);

  useEffect(() => {
    loadView();
  }, [loadView]);

  // ─── Handlers ───
  const handleOpenFolder = (folder) => {
    setFolders([]);
    setResults([]);
    setKeyword('');
    setCurrentFolderId(folder.id);
    setFolderPath((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setIsCategoryDrawerOpen(false);
  };

  const handleBreadcrumbClick = (index) => {
    if (index < 0) return;
    const newPath = folderPath.slice(0, index + 1);
    setFolders([]);
    setResults([]);
    setKeyword('');
    setFolderPath(newPath);
    setCurrentFolderId(newPath[newPath.length - 1].id);
  };

  const handleCategoryClick = (categoryLabel) => {
    setFolders([]);
    setResults([]);
    setActiveCategory(categoryLabel);
    setIsCategoryDrawerOpen(false);
    setCurrentFolderId(null);
    setFolderPath([{ id: null, name: categoryLabel }]);
    setKeyword('');
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    setLoading(true);
    try {
      const folderPayload = {
        name: newFolderName.trim(),
        description: newFolderDesc,
        parentFolderId: currentFolderId,
        scanType: scanEnum,
      };

      const res = await api.createFolder(folderPayload);
      const createdFolder = res?.data;

      if (createdFolder && createdFolder.id !== undefined) {
        rememberFolder(createdFolder.id, scanEnum, currentFolderId);
        setFolders((prev) => [{ ...createdFolder, reportCount: 0 }, ...prev]);
      }

      setShowFolderModal(false);
      setNewFolderName('');
      setNewFolderDesc('');

      if (!createdFolder || createdFolder.id === undefined) {
        await loadView();
      }
    } catch (error) {
      alert('Failed to create folder: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!keyword.trim()) return;
    setLoading(true);
    try {
      const filters = {
        query: keyword,
        scanType: scanEnum,
        page: 0,
        size: 50,
      };
      if (currentFolderId !== null) filters.folderId = currentFolderId;

      const res = await api.searchReportsPage(filters);
      const list = res?.data?.content || res?.data || [];
      const mapped = filterServerItems(list, scanEnum, currentFolderId).map(mapReport);

      const q = keyword.trim().toLowerCase();
      const localMap = readLS('uploads', {});
      const localMatches = (localMap[scopeKey(scanEnum, currentFolderId)] || []).filter(
        (i) => (i.title || '').toLowerCase().includes(q)
      );
      const ids = new Set(mapped.map((i) => String(i.id)));

      setResults(
        applyRenames([...mapped, ...localMatches.filter((i) => !ids.has(String(i.id)))])
      );
      setFolders([]);
    } catch (error) {
      console.error('[SonoVault] Search failed:', error);
      alert('Search failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setKeyword('');
  };

  const handleFileUpload = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    if (files.length === 0) return;

    const targetEnum = scanEnum;
    const targetFolderId = currentFolderId;
    const targetName = insideFolder ? currentFolder.name : activeCategory;

    setLoading(true);
    try {
      const res = await api.uploadVaultDocumentsBatch(files, {
        folderId: targetFolderId,
        scanType: targetEnum,
      });

      const d = res?.data;
      const echoed = Array.isArray(d)
        ? d
        : Array.isArray(d?.documents)
        ? d.documents
        : Array.isArray(d?.content)
        ? d.content
        : d && typeof d === 'object' && d.id
        ? [d]
        : [];

      const now = Date.now();
      const newItems = files.map((f, i) => {
        const doc = echoed[i];
        const name = doc?.originalFilename || doc?.filename || doc?.name || f.name;
        const itemId = doc?.id ?? `local-${now}-${i}`;

        localFilesRef.current[String(itemId)] = f;

        return {
          id: itemId,
          title: name,
          filename: name,
          isUpload: true,
          type: targetEnum,
          content: 'Uploaded document',
          fullContent: `Uploaded document: ${name}\n\nSize: ${(f.size / 1024).toFixed(
            1
          )} KB`,
          scanDate: new Date().toISOString(),
          status: 'UPLOADED',
          local: true,
        };
      });

      const localMap = readLS('uploads', {});
      const key = scopeKey(targetEnum, targetFolderId);
      localMap[key] = [...newItems, ...(localMap[key] || [])];
      writeLS('uploads', localMap);

      alert(`Successfully added ${files.length} document(s) to "${targetName}".`);

      if (keyword) setKeyword('');
      else await loadView();
    } catch (error) {
      console.error('[SonoVault] Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setLoading(false);
      input.value = null;
    }
  };

  // ─── Delete a report / uploaded file / folder ───
  const openDelete = (target, kind = 'report') => {
    setDeleteError('');
    setDeleteTarget({ ...target, __kind: kind });
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    const isFolder = target.__kind === 'folder';
    const idStr = String(target.id);

    setDeleting(true);
    setDeleteError('');
    try {
      // ── Delete a FOLDER (and everything inside it) ──
      if (isFolder) {
        // Best-effort server delete. If the endpoint is missing (405/404),
        // we still purge locally so the UI stays consistent.
        if (!isLocalId(idStr)) {
          try {
            await vaultRequest(
              'DELETE',
              `/vault/folders/${encodeURIComponent(idStr)}`
            );
          } catch (err) {
            const msg = String(err.message || '');
            // 404 = already gone; 405/501 = endpoint not implemented.
            if (!/\(404\)|\(405\)|\(501\)/.test(msg)) {
              // Any other failure (403 etc.) should stop the delete.
              throw err;
            }
          }
        }

        // If the folder we're deleting (or a descendant) is currently open,
        // pop back to its parent so we never point at a dead folder.
        const removedIds = purgeFolderLocally(idStr);
        const removedSet = new Set(removedIds.map(String));

        setFolderPath((prevPath) => {
          if (!removedSet.has(String(currentFolderId))) return prevPath;
          // Walk back until we find a folder that still exists
          for (let i = prevPath.length - 1; i >= 0; i -= 1) {
            if (!removedSet.has(String(prevPath[i].id))) {
              const trimmed = prevPath.slice(0, i + 1);
              setCurrentFolderId(trimmed[trimmed.length - 1].id);
              return trimmed;
            }
          }
          setCurrentFolderId(null);
          return [{ id: null, name: activeCategory }];
        });

        setFolders((prev) =>
          prev.filter((f) => !removedSet.has(String(f.id)))
        );
        setDeleteTarget(null);
        if (!keyword) await loadView();
        return;
      }

      // ── Delete a REPORT or UPLOADED FILE ──
      if (!isLocalId(idStr)) {
        try {
          await vaultRequest('DELETE', `/vault/reports/${encodeURIComponent(idStr)}`);
        } catch (err) {
          if (!/\(404\)/.test(err.message)) throw err;
        }
      }

      removeLocalItem(idStr);
      delete localFilesRef.current[idStr];

      setResults((prev) => prev.filter((r) => String(r.id) !== idStr));
      if (previewReport && String(previewReport.id) === idStr) setPreviewReport(null);
      setDeleteTarget(null);

      if (!keyword) await loadView();
    } catch (err) {
      console.error('[SonoVault] Delete failed:', err);
      setDeleteError(err.message || 'Failed to delete.');
    } finally {
      setDeleting(false);
    }
  };

  // ─── Edit / update a report or uploaded file ───
  const openEdit = async (report) => {
    setEditError('');
    setEditFull(null);
    setEditForm({ ...EDIT_FORM_INITIAL, name: report.title || '' });
    setEditTarget(report);

    if (report.isUpload) return;

    if (isLocalId(report.id)) {
      setEditError('This item only exists in this browser and cannot be edited.');
      return;
    }

    setEditLoading(true);
    try {
      const res = await vaultRequest(
        'GET',
        `/vault/reports/${encodeURIComponent(String(report.id))}`
      );
      const d = res?.data || {};
      setEditFull(d);
      setEditForm({
        name: d.patientName || report.title || '',
        patientName: d.patientName || '',
        patientAge: d.patientAge ?? '',
        patientSex: d.patientSex || '',
        patientId: d.patientId || '',
        scanDate: d.scanDate || '',
        clinicalHistory: d.clinicalHistory || '',
        findings: d.findings || '',
        impression: d.impression || '',
        recommendation: d.recommendation || '',
      });
    } catch (err) {
      console.error('[SonoVault] Failed to load report for editing:', err);
      setEditError('Could not load this report: ' + err.message);
    } finally {
      setEditLoading(false);
    }
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditTarget(null);
    setEditFull(null);
    setEditError('');
  };

  const setField = (key) => (e) => {
    const value = e.target.value;
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;

    const idStr = String(editTarget.id);
    setEditSaving(true);
    setEditError('');

    try {
      if (editTarget.isUpload) {
        const newName = editForm.name.trim();
        if (!newName) {
          setEditError('Name cannot be empty.');
          setEditSaving(false);
          return;
        }

        if (!isLocalId(idStr)) {
          try {
            const res = await vaultRequest(
              'GET',
              `/vault/reports/${encodeURIComponent(idStr)}`
            );
            const payload = { ...pickEditable(res?.data), patientName: newName };
            await vaultRequest('PUT', `/vault/reports/${encodeURIComponent(idStr)}`, payload);
          } catch (err) {
            console.warn('[SonoVault] Server rename skipped:', err.message);
          }
        }

        const renames = readLS('renames', {});
        renames[idStr] = newName;
        writeLS('renames', renames);

        setResults((prev) =>
          prev.map((r) => (String(r.id) === idStr ? { ...r, title: newName } : r))
        );
        if (previewReport && String(previewReport.id) === idStr) {
          setPreviewReport((p) => (p ? { ...p, title: newName } : p));
        }

        setEditTarget(null);
        return;
      }

      if (!editFull) {
        setEditError('The report has not finished loading.');
        setEditSaving(false);
        return;
      }

      const payload = {
        ...pickEditable(editFull),
        patientName: editForm.patientName,
        patientAge: editForm.patientAge === '' ? undefined : Number(editForm.patientAge),
        patientSex: editForm.patientSex || undefined,
        patientId: editForm.patientId,
        scanDate: editForm.scanDate || undefined,
        clinicalHistory: editForm.clinicalHistory,
        findings: editForm.findings,
        impression: editForm.impression,
        recommendation: editForm.recommendation,
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      const res = await vaultRequest(
        'PUT',
        `/vault/reports/${encodeURIComponent(idStr)}`,
        payload
      );

      const updated = res?.data ? mapReport(res.data) : null;
      const fresh = updated || mapReport({ ...editFull, ...payload, id: editTarget.id });

      const renames = readLS('renames', {});
      if (renames[idStr]) {
        delete renames[idStr];
        writeLS('renames', renames);
      }

      setResults((prev) => prev.map((r) => (String(r.id) === idStr ? fresh : r)));
      if (previewReport && String(previewReport.id) === idStr) setPreviewReport(fresh);
      setEditTarget(null);
      setEditFull(null);
    } catch (err) {
      console.error('[SonoVault] Update failed:', err);
      setEditError('Failed to save: ' + err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const handleMenuClick = (tab) => {
    setIsMenuOpen(false);
    setIsCategoryDrawerOpen(false);
    if (onNavigate) onNavigate(tab);
    else if (setActiveTab) setActiveTab(tab);
    else if (setTab) setTab(tab);
    else if (onTabChange) onTabChange(tab);
    if (onBack) onBack(tab);
  };

  const MAX_STORAGE_GB = 100;
  const storageUsedMB = results.length * 1.5;
  const storageUsedGB = (storageUsedMB / 1024).toFixed(3);
  const storagePercentage = Math.min(
    (storageUsedMB / 1024 / MAX_STORAGE_GB) * 100,
    100
  ).toFixed(1);

  const railWidth = isDesktop ? 280 : 0;
  const showRail = isDesktop;
  const contentPadding = isMobile ? '12px' : isTablet ? '18px' : '25px';
  const headerPadding = isMobile ? '12px 16px' : '15px 30px';

  const renderCategories = () =>
    SCAN_ENUMS.map((cat) => {
      const isActive = activeCategory === cat.label;
      return (
        <div
          key={cat.value}
          onClick={() => handleCategoryClick(cat.label)}
          className="vault-category-item"
          style={{
            backgroundColor: isActive ? '#3b82f6' : 'transparent',
            color: isActive ? 'white' : '#cbd5e1',
            fontWeight: isActive ? '600' : '400',
          }}
        >
          <Svg size={18}>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </Svg>
          {cat.label}
        </div>
      );
    });

  const labelInput = {
    width: '100%',
    padding: '10px',
    borderRadius: '6px',
    border: '1px solid #cbd5e1',
    boxSizing: 'border-box',
    fontSize: '0.95rem',
  };
  const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#475569',
  };

  const previewBtn = {
    padding: '8px 14px',
    borderRadius: '6px',
    fontWeight: '600',
    fontSize: '0.85rem',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  };

  const cardBtnBase = {
    padding: '8px 14px',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
    flex: isMobile ? 1 : 'none',
  };

  const modalOverlay = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 10002,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    boxSizing: 'border-box',
  };

  return (
    <>
      <style>{`
        .slide-menu-item {
          padding: 14px 18px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 12px;
          color: #0f172a !important;
          font-weight: 600;
          transition: all 0.2s;
          font-size: 1rem;
          background: transparent;
        }
        .slide-menu-item:hover {
          background-color: #eff6ff;
          color: #2563eb !important;
        }
        .slide-menu-item svg {
          color: #2563eb;
          flex-shrink: 0;
        }

        .vault-result-card {
          transition: all 0.2s ease;
        }
        .vault-result-card:hover {
          border-color: #3b82f6 !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important;
          transform: translateY(-1px);
        }

        .vault-folder-card {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 15px 20px;
          background: #f8fafc;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .vault-folder-card:hover {
          background: #f1f5f9;
          border-color: #94a3b8;
        }

        .vault-folder-delete {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 6px;
          border-radius: 6px;
          border: 1px solid #fecaca;
          background: white;
          color: #dc2626;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease;
        }
        .vault-folder-delete:hover {
          background: #fef2f2;
          border-color: #f87171;
        }

        .vault-category-item {
          padding: 12px 20px;
          margin: 4px 15px 4px 0;
          cursor: pointer;
          border-radius: 0 25px 25px 0;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: all 0.2s;
          font-size: 0.85rem;
          flex-shrink: 0;
        }
        .vault-category-item:hover {
          background-color: rgba(59, 130, 246, 0.15);
        }

        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>

      {loading && (
        <div className="fullscreen-loader-overlay">
          <div className="dot-loader">
            <div></div>
            <div></div>
            <div></div>
          </div>
          <div className="loading-text">Loading Vault...</div>
        </div>
      )}

      {isMenuOpen && (
        <>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.5)',
              zIndex: 9998,
              backdropFilter: 'blur(2px)',
            }}
            onClick={() => setIsMenuOpen(false)}
          />
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '300px',
              maxWidth: '85vw',
              height: '100vh',
              backgroundColor: '#ffffff',
              zIndex: 9999,
              boxShadow: '4px 0 25px rgba(0,0,0,0.15)',
              display: 'flex',
              flexDirection: 'column',
              animation: 'slideIn 0.3s ease forwards',
            }}
          >
            <div
              style={{
                padding: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e2e8f0',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img
                  src={process.env.PUBLIC_URL + '/echionLOGO.png'}
                  alt="Logo"
                  style={{ width: '32px', height: '32px' }}
                />
                <span
                  style={{
                    fontSize: '1.15rem',
                    fontWeight: '800',
                    color: '#0f172a',
                  }}
                >
                  Echion Menu
                </span>
              </div>
              <button
                onClick={() => setIsMenuOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  padding: '6px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                aria-label="Close menu"
              >
                <Svg size={24}>
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </Svg>
              </button>
            </div>

            <div
              style={{
                flex: 1,
                padding: '16px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                overflowY: 'auto',
              }}
            >
              {MENU_ITEMS.map((item) => (
                <div
                  key={item.tab}
                  onClick={() => handleMenuClick(item.tab)}
                  className="slide-menu-item"
                >
                  <Svg size={20}>
                    {item.d.split(' M').map((seg, i) => (
                      <path key={i} d={i === 0 ? seg : 'M' + seg} />
                    ))}
                  </Svg>
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {isCategoryDrawerOpen && !isDesktop && (
        <>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.5)',
              zIndex: 9998,
              backdropFilter: 'blur(2px)',
            }}
            onClick={() => setIsCategoryDrawerOpen(false)}
          />
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '300px',
              maxWidth: '85vw',
              height: '100vh',
              backgroundColor: '#1e293b',
              color: 'white',
              zIndex: 9999,
              boxShadow: '4px 0 25px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
              animation: 'slideIn 0.3s ease forwards',
            }}
          >
            <div
              style={{
                padding: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <span style={{ fontSize: '1.1rem', fontWeight: '700', color: 'white' }}>
                Categories
              </span>
              <button
                onClick={() => setIsCategoryDrawerOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#cbd5e1',
                  padding: '6px',
                }}
                aria-label="Close categories"
              >
                <Svg size={24}>
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </Svg>
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
              {renderCategories()}
            </div>
          </div>
        </>
      )}

      {showFolderModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              width: '440px',
              maxWidth: '100%',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              boxSizing: 'border-box',
            }}
          >
            <h3 style={{ margin: '0 0 6px 0', color: '#0f172a' }}>
              Create New Folder
            </h3>
            <p style={{ margin: '0 0 15px 0', fontSize: '0.85rem', color: '#64748b' }}>
              In <strong>{activeCategory}</strong>
              {insideFolder ? ` / ${currentFolder.name}` : ''}
            </p>
            <form onSubmit={handleCreateFolder}>
              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Folder Name *</label>
                <input
                  required
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  style={labelInput}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Description (Optional)</label>
                <input
                  type="text"
                  value={newFolderDesc}
                  onChange={(e) => setNewFolderDesc(e.target.value)}
                  style={labelInput}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowFolderModal(false)}
                  style={{
                    padding: '10px 18px',
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    color: '#475569',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 18px',
                    background: '#3b82f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    color: 'white',
                  }}
                >
                  Create Folder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Delete confirmation modal (reports, files AND folders) ─── */}
      {deleteTarget && (
        <div style={{ ...modalOverlay, zIndex: 10003 }} onClick={closeDelete}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              width: '420px',
              maxWidth: '100%',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              boxSizing: 'border-box',
            }}
          >
            <h3 style={{ margin: '0 0 10px 0', color: '#0f172a' }}>
              {deleteTarget.__kind === 'folder'
                ? 'Delete folder?'
                : deleteTarget.isUpload
                ? 'Delete file?'
                : 'Delete report?'}
            </h3>
            <p style={{ margin: '0 0 8px 0', color: '#475569', lineHeight: 1.5 }}>
              <strong style={{ wordBreak: 'break-word' }}>{deleteTarget.title || deleteTarget.name}</strong>{' '}
              {deleteTarget.__kind === 'folder'
                ? 'and everything inside it (including subfolders and files) will be permanently removed from your vault. This cannot be undone.'
                : 'will be permanently removed from your vault. This cannot be undone.'}
            </p>
            {deleteError && (
              <p style={{ margin: '8px 0 0 0', color: '#b91c1c', fontSize: '0.85rem' }}>
                {deleteError}
              </p>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                marginTop: '20px',
              }}
            >
              <button
                type="button"
                onClick={closeDelete}
                disabled={deleting}
                style={{
                  padding: '10px 18px',
                  background: '#f1f5f9',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  color: '#475569',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                style={{
                  padding: '10px 18px',
                  background: '#dc2626',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  color: 'white',
                  opacity: deleting ? 0.7 : 1,
                }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editTarget && (
        <div style={modalOverlay} onClick={closeEdit}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              width: editTarget.isUpload ? '440px' : '620px',
              maxWidth: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              boxSizing: 'border-box',
            }}
          >
            <h3 style={{ margin: '0 0 6px 0', color: '#0f172a' }}>
              {editTarget.isUpload ? 'Rename File' : 'Edit Report'}
            </h3>
            <p style={{ margin: '0 0 15px 0', fontSize: '0.85rem', color: '#64748b' }}>
              {editTarget.isUpload
                ? 'Change the name shown for this file in your vault.'
                : 'Changes are saved to the server and a version history is kept.'}
            </p>

            {editLoading ? (
              <div style={{ padding: '30px 0', textAlign: 'center', color: '#475569' }}>
                Loading report...
              </div>
            ) : (
              <form onSubmit={saveEdit}>
                {editTarget.isUpload ? (
                  <div style={{ marginBottom: '15px' }}>
                    <label style={labelStyle}>File Name *</label>
                    <input
                      required
                      type="text"
                      value={editForm.name}
                      onChange={setField('name')}
                      style={labelInput}
                    />
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: '12px',
                        marginBottom: '12px',
                      }}
                    >
                      <div>
                        <label style={labelStyle}>Patient Name</label>
                        <input
                          type="text"
                          value={editForm.patientName}
                          onChange={setField('patientName')}
                          style={labelInput}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Patient ID</label>
                        <input
                          type="text"
                          value={editForm.patientId}
                          onChange={setField('patientId')}
                          style={labelInput}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Age</label>
                        <input
                          type="number"
                          min="0"
                          value={editForm.patientAge}
                          onChange={setField('patientAge')}
                          style={labelInput}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Sex</label>
                        <select
                          value={editForm.patientSex}
                          onChange={setField('patientSex')}
                          style={labelInput}
                        >
                          <option value="">Select...</option>
                          <option value="MALE">Male</option>
                          <option value="FEMALE">Female</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Scan Date</label>
                        <input
                          type="date"
                          value={String(editForm.scanDate || '').substring(0, 10)}
                          onChange={setField('scanDate')}
                          style={labelInput}
                        />
                      </div>
                    </div>

                    {[
                      ['clinicalHistory', 'Clinical History', 3],
                      ['findings', 'Findings', 6],
                      ['impression', 'Impression', 4],
                      ['recommendation', 'Recommendation', 3],
                    ].map(([key, label, rows]) => (
                      <div key={key} style={{ marginBottom: '12px' }}>
                        <label style={labelStyle}>{label}</label>
                        <textarea
                          rows={rows}
                          value={editForm[key]}
                          onChange={setField(key)}
                          style={{ ...labelInput, resize: 'vertical', fontFamily: 'inherit' }}
                        />
                      </div>
                    ))}
                  </>
                )}

                {editError && (
                  <p style={{ margin: '0 0 12px 0', color: '#b91c1c', fontSize: '0.85rem' }}>
                    {editError}
                  </p>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={closeEdit}
                    disabled={editSaving}
                    style={{
                      padding: '10px 18px',
                      background: '#f1f5f9',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: editSaving ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      color: '#475569',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editSaving || (!editTarget.isUpload && !editFull)}
                    style={{
                      padding: '10px 18px',
                      background: '#3b82f6',
                      border: 'none',
                      borderRadius: '6px',
                      cursor:
                        editSaving || (!editTarget.isUpload && !editFull)
                          ? 'not-allowed'
                          : 'pointer',
                      fontWeight: '600',
                      color: 'white',
                      opacity: editSaving || (!editTarget.isUpload && !editFull) ? 0.6 : 1,
                    }}
                  >
                    {editSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ─── Main Full-Screen Layout ─── */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          width: '100vw',
          height: '100vh',
          fontFamily: "'Inter', 'Segoe UI', sans-serif",
          backgroundColor: '#f8fafc',
          overflow: 'hidden',
          zIndex: 900,
        }}
      >
        {showRail && (
          <div
            style={{
              width: `${railWidth}px`,
              backgroundColor: '#1e293b',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <button
                onClick={() => handleMenuClick('report')}
                title="Back to Home"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '5px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Svg size={24}>
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </Svg>
                <span style={{ fontSize: '0.75rem', marginTop: '4px', fontWeight: '600' }}>
                  Home
                </span>
              </button>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginLeft: '10px',
                }}
              >
                <div
                  style={{
                    background: '#3b82f6',
                    borderRadius: '50%',
                    padding: '4px',
                    display: 'flex',
                  }}
                >
                  <Svg size={20} stroke="white">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  </Svg>
                </div>
                <span
                  style={{
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    letterSpacing: '0.5px',
                  }}
                >
                  Sonovault
                </span>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '15px 0' }}>
              {renderCategories()}
            </div>
          </div>
        )}

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <div
            style={{
              backgroundColor: '#1e293b',
              padding: headerPadding,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: '#cbd5e1',
              flexShrink: 0,
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              gap: '12px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
                minWidth: 0,
                flex: 1,
              }}
            >
              <button
                onClick={() => setIsMenuOpen(true)}
                aria-label="Open main menu"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '6px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Svg size={22}>
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </Svg>
              </button>

              {!isDesktop && (
                <button
                  onClick={() => handleMenuClick('report')}
                  title="Home"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Svg size={22}>
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </Svg>
                </button>
              )}

              {!isDesktop && (
                <button
                  onClick={() => setIsCategoryDrawerOpen(true)}
                  style={{
                    background: '#334155',
                    border: '1px solid #475569',
                    color: 'white',
                    cursor: 'pointer',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Svg size={14}>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </Svg>
                  {activeCategory}
                </button>
              )}

              {!isMobile && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Storage Usage:{' '}
                    <span style={{ color: 'white' }}>
                      {storageUsedGB > 0.01 ? storageUsedGB : '< 0.01'} GB used of{' '}
                      {MAX_STORAGE_GB} GB
                    </span>
                  </span>
                  <div
                    style={{
                      width: '180px',
                      height: '6px',
                      backgroundColor: '#334155',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      flexShrink: 1,
                    }}
                  >
                    <div
                      style={{
                        width: `${storagePercentage}%`,
                        height: '100%',
                        backgroundColor: '#3b82f6',
                      }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                gap: '8px',
                flexShrink: 0,
                width: isMobile ? '100%' : 'auto',
                justifyContent: isMobile ? 'stretch' : 'flex-end',
              }}
            >
              <button
                onClick={() => setShowFolderModal(true)}
                style={{
                  backgroundColor: '#334155',
                  color: 'white',
                  border: '1px solid #475569',
                  padding: isMobile ? '8px 10px' : '10px 16px',
                  borderRadius: '6px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  flex: isMobile ? 1 : 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                <Svg size={16}>
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                  <line x1="12" y1="11" x2="12" y2="17"></line>
                  <line x1="9" y1="14" x2="15" y2="14"></line>
                </Svg>
                {isMobile ? 'Folder' : 'New Folder'}
              </button>

              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                multiple
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleFileUpload}
              />
              <button
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: isMobile ? '8px 10px' : '10px 16px',
                  borderRadius: '6px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  flex: isMobile ? 1 : 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                <Svg size={16}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </Svg>
                {isMobile ? 'Upload' : insideFolder ? 'Upload to Folder' : 'Upload File'}
              </button>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              margin: contentPadding,
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <div
              style={{
                padding: contentPadding,
                borderBottom: '1px solid #e2e8f0',
                backgroundColor: '#ffffff',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '15px',
                  color: '#64748b',
                  fontSize: '0.85rem',
                  fontWeight: '500',
                  flexWrap: 'wrap',
                }}
              >
                {folderPath.map((node, i) => (
                  <React.Fragment key={node.id || 'root'}>
                    <span
                      onClick={() => handleBreadcrumbClick(i)}
                      style={{
                        cursor: 'pointer',
                        color: i === folderPath.length - 1 ? '#0f172a' : '#3b82f6',
                      }}
                    >
                      {node.name}
                    </span>
                    {i < folderPath.length - 1 && <span>/</span>}
                  </React.Fragment>
                ))}
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'stretch',
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                  <svg
                    width="20"
                    height="20"
                    fill="none"
                    stroke="#94a3b8"
                    strokeWidth="2"
                    style={{
                      position: 'absolute',
                      left: '15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                    viewBox="0 0 24 24"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <input
                    type="text"
                    placeholder={
                      insideFolder
                        ? `Search in ${currentFolder.name}...`
                        : 'Search in current folder...'
                    }
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    style={{
                      width: '100%',
                      padding: '12px 15px 12px 45px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '0.95rem',
                      outlineColor: '#3b82f6',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={handleSearch}
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      flex: isMobile ? 1 : 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Search
                  </button>
                  <button
                    onClick={handleClear}
                    style={{
                      backgroundColor: 'white',
                      color: '#475569',
                      border: '1px solid #cbd5e1',
                      padding: '12px 20px',
                      borderRadius: '6px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      flex: isMobile ? 1 : 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Svg size={16}>
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </Svg>
                    Clear
                  </button>
                </div>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: contentPadding,
                backgroundColor: '#ffffff',
                minHeight: 0,
              }}
            >
              {insideFolder && !keyword && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    padding: '14px 16px',
                    marginBottom: '20px',
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '10px',
                    flexWrap: 'wrap',
                  }}
                >
                  <button
                    onClick={() => handleBreadcrumbClick(folderPath.length - 2)}
                    style={{
                      background: 'white',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      color: '#334155',
                      fontWeight: '600',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <Svg size={16}>
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </Svg>
                    Back
                  </button>

                  <div style={{ color: '#2563eb', display: 'flex' }}>
                    <Svg size={34}>
                      <path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />
                    </Svg>
                  </div>

                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div
                      style={{
                        fontWeight: '800',
                        color: '#0f172a',
                        fontSize: '1.05rem',
                        wordBreak: 'break-word',
                      }}
                    >
                      {currentFolder.name}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                      Folder opened in {activeCategory} •{' '}
                      {folders.length + results.length} item(s)
                    </div>
                  </div>

                  <button
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      padding: '9px 16px',
                      borderRadius: '6px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '0.85rem',
                      width: isMobile ? '100%' : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    <Svg size={16}>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </Svg>
                    Upload files here
                  </button>
                </div>
              )}

              <h3
                style={{
                  margin: '0 0 20px 0',
                  fontSize: '1.05rem',
                  color: '#0f172a',
                  fontWeight: '700',
                }}
              >
                {keyword
                  ? 'Search Results'
                  : insideFolder
                  ? `Inside “${currentFolder.name}”`
                  : `${activeCategory} - Folder Contents`}
              </h3>

              {!loading && folders.length === 0 && results.length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    color: '#94a3b8',
                    padding: '40px 16px',
                  }}
                >
                  {keyword
                    ? 'No results match your search.'
                    : insideFolder
                    ? 'This folder is empty. Use “Upload files here” to add documents.'
                    : `No items found in ${activeCategory}.`}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Folders */}
                {!keyword &&
                  folders.map((folder) => (
                    <div
                      key={folder.id}
                      className="vault-folder-card"
                      onClick={() => handleOpenFolder(folder)}
                      style={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
                    >
                      <svg
                        width="32"
                        height="32"
                        fill="#3b82f6"
                        viewBox="0 0 24 24"
                        style={{ flexShrink: 0 }}
                      >
                        <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
                      </svg>
                      <div style={{ flex: 1, minWidth: isMobile ? '100%' : 0 }}>
                        <h4
                          style={{
                            margin: '0 0 4px 0',
                            fontSize: '1rem',
                            color: '#0f172a',
                            fontWeight: '700',
                          }}
                        >
                          {folder.name}
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
                          {folder.description || 'No description'}
                        </p>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: '#94a3b8',
                            fontWeight: '600',
                          }}
                        >
                          {folder.reportCount ?? 0} Items
                        </div>
                        <button
                          type="button"
                          className="vault-folder-delete"
                          title="Delete folder"
                          aria-label={`Delete folder ${folder.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            openDelete(
                              {
                                id: folder.id,
                                title: folder.name,
                                name: folder.name,
                              },
                              'folder'
                            );
                          }}
                        >
                          <Svg size={16}>
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                            <path d="M10 11v6"></path>
                            <path d="M14 11v6"></path>
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                          </Svg>
                        </button>
                      </div>
                    </div>
                  ))}

                {/* Reports / uploaded files */}
                {results.map((report) => (
                  <div
                    key={report.id}
                    className="vault-result-card"
                    onClick={() => setPreviewReport(report)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: isMobile ? '14px' : '18px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.01)',
                      cursor: 'pointer',
                      gap: '12px',
                      flexWrap: isMobile ? 'wrap' : 'nowrap',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <h4
                          style={{
                            margin: '0 0 6px 0',
                            fontSize: isMobile ? '0.95rem' : '1.02rem',
                            color: '#0f172a',
                            fontWeight: '700',
                            wordBreak: 'break-word',
                          }}
                        >
                          {report.title}
                        </h4>
                        <p
                          style={{
                            margin: 0,
                            fontSize: '0.85rem',
                            color: '#475569',
                            wordBreak: 'break-word',
                          }}
                        >
                          <span style={{ fontWeight: '600', color: '#334155' }}>
                            Preview:
                          </span>{' '}
                          {report.content}
                        </p>
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        flexShrink: 0,
                        width: isMobile ? '100%' : 'auto',
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewReport(report);
                        }}
                        style={{
                          ...cardBtnBase,
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                        }}
                      >
                        <Svg size={16}>
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </Svg>
                        View
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(report);
                        }}
                        title={report.isUpload ? 'Rename file' : 'Edit report'}
                        style={{
                          ...cardBtnBase,
                          backgroundColor: 'white',
                          color: '#334155',
                          border: '1px solid #cbd5e1',
                        }}
                      >
                        <Svg size={16}>
                          <path d="M12 20h9"></path>
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                        </Svg>
                        Edit
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDelete(report, 'report');
                        }}
                        title="Delete"
                        style={{
                          ...cardBtnBase,
                          backgroundColor: 'white',
                          color: '#dc2626',
                          border: '1px solid #fecaca',
                        }}
                      >
                        <Svg size={16}>
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                          <path d="M10 11v6"></path>
                          <path d="M14 11v6"></path>
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                        </Svg>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                padding: '12px 20px',
                backgroundColor: '#f8fafc',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <span style={{ fontSize: '0.9rem', color: '#475569', fontWeight: '500' }}>
                Total Items:{' '}
                <span style={{ color: '#3b82f6', fontWeight: '700' }}>
                  {folders.length + results.length}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {previewReport && (
        <div
          onClick={() => setPreviewReport(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.55)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '8px' : '16px',
            boxSizing: 'border-box',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '980px',
              maxWidth: '100%',
              height: '92vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxSizing: 'border-box',
              boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: isMobile ? '10px 12px' : '12px 18px',
                borderBottom: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                flexShrink: 0,
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  fontWeight: '700',
                  color: '#0f172a',
                  fontSize: isMobile ? '0.95rem' : '1.05rem',
                  minWidth: 0,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={previewReport.title}
              >
                {previewReport.title}
              </div>

              <div style={{ display: 'flex', gap: '8px', flexShrink: 0, flexWrap: 'wrap' }}>
                {viewer.status === 'ready' && viewer.url && (
                  <>
                    {(viewer.kind === 'pdf' || viewer.kind === 'image') && (
                      <a
                        href={viewer.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          ...previewBtn,
                          backgroundColor: 'white',
                          color: '#334155',
                          border: '1px solid #cbd5e1',
                        }}
                      >
                        Open in new tab
                      </a>
                    )}
                    <a
                      href={viewer.url}
                      download={previewReport.filename || previewReport.title || 'document'}
                      style={{
                        ...previewBtn,
                        backgroundColor: 'white',
                        color: '#334155',
                        border: '1px solid #cbd5e1',
                      }}
                    >
                      Download
                    </a>
                  </>
                )}
                <button
                  onClick={() => openEdit(previewReport)}
                  style={{
                    ...previewBtn,
                    backgroundColor: 'white',
                    color: '#334155',
                    border: '1px solid #cbd5e1',
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => openDelete(previewReport, 'report')}
                  style={{
                    ...previewBtn,
                    backgroundColor: 'white',
                    color: '#dc2626',
                    border: '1px solid #fecaca',
                  }}
                >
                  Delete
                </button>
                <button
                  onClick={() => setPreviewReport(null)}
                  style={{
                    ...previewBtn,
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                backgroundColor: viewer.kind === 'pdf' ? '#525659' : '#f1f5f9',
                position: 'relative',
              }}
            >
              {viewer.status === 'loading' && (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#475569',
                    fontWeight: '600',
                  }}
                >
                  Loading document...
                </div>
              )}

              {viewer.status === 'error' && (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '24px',
                    color: '#b91c1c',
                    lineHeight: 1.6,
                  }}
                >
                  {viewer.message}
                </div>
              )}

              {viewer.status === 'ready' && viewer.kind === 'pdf' && (
                <iframe
                  title={previewReport.title}
                  src={viewer.url}
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                />
              )}

              {viewer.status === 'ready' && viewer.kind === 'image' && (
                <div style={{ padding: '16px', textAlign: 'center' }}>
                  <img
                    src={viewer.url}
                    alt={previewReport.title}
                    style={{ maxWidth: '100%', height: 'auto', backgroundColor: 'white' }}
                  />
                </div>
              )}

              {viewer.status === 'ready' && viewer.kind === 'text' && (
                <div
                  style={{
                    maxWidth: '820px',
                    margin: '0 auto',
                    padding: isMobile ? '20px' : '40px',
                    backgroundColor: 'white',
                    minHeight: '100%',
                    boxSizing: 'border-box',
                    color: '#1e293b',
                    lineHeight: 1.7,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                  }}
                >
                  {viewer.text}
                </div>
              )}

              {viewer.status === 'ready' && viewer.kind === 'other' && (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '24px',
                    color: '#475569',
                    lineHeight: 1.6,
                  }}
                >
                  {viewer.message}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SonoVault;